import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DetailField, DetailFieldGrid } from '../DetailField';
import { DetailSection } from '../DetailSection';
import {
    findFact,
    POI_KEY_FACT_TYPES,
    SUBJECT_KEY_FACT_TYPES,
    type FactLike,
} from './factTypes';
import { FactValueView } from './FactValueView';

type EntityKind = 'subject' | 'poi';

const KEY_FACT_TYPES: Record<EntityKind, readonly string[]> = {
    subject: SUBJECT_KEY_FACT_TYPES,
    poi: POI_KEY_FACT_TYPES,
};

const KEY_FACT_LABELS: Record<string, string> = {
    known_loan_amount: 'factTypes.known_loan_amount',
    financial_obligations: 'factTypes.financial_obligations',
    financial_exposure: 'factTypes.financial_exposure',
    verification_status: 'factTypes.verification_status',
    claimed_location: 'factTypes.claimed_location',
    last_field_report: 'factTypes.last_field_report',
};

export function KeyFactsSummary({
    entityKind,
    facts,
}: {
    entityKind: EntityKind;
    facts: FactLike[];
}) {
    const { t } = useTranslation();

    const keyFacts = useMemo(() => {
        const types = KEY_FACT_TYPES[entityKind];
        return types
            .map((factType) => findFact(facts, factType))
            .filter((fact): fact is FactLike => Boolean(fact));
    }, [entityKind, facts]);

    if (keyFacts.length === 0) {
        return null;
    }

    return (
        <DetailSection title={t('detail.keyFacts')} description={t('detail.keyFactsHint')}>
            <DetailFieldGrid>
                {keyFacts.map((fact) => (
                    <DetailField
                        key={fact.id}
                        label={t(KEY_FACT_LABELS[fact.factType] ?? `factTypes.${fact.factType}`, {
                            defaultValue: fact.factType,
                        })}
                        value={
                            <FactValueView
                                recordType={fact.factType}
                                value={fact.factValue ?? {}}
                                compact
                            />
                        }
                    />
                ))}
            </DetailFieldGrid>
        </DetailSection>
    );
}
